'use client'

import { useEffect, useRef, useState } from 'react'
import { legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import type { EditorSettingName } from '@/msgdeskRuntime/contracts/editorModel'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { EditorBodyFields } from '@/deskUiSkeletons/editor/EditorBodyFields'
import { contract } from '@/deskUiSkeletons/editor/Editor.helpers'
import { EditorHeadFields, HeadShort } from '@/deskUiSkeletons/editor/components/Head/Head'
import { EditorConfigureModal } from '@/deskUiSkeletons/editor/components/Modal/ConfigureModal'
import { EditorAttachmentsModal, editorAttachmentCount } from '@/deskUiSkeletons/editor/components/Modal/AttachmentsModal'
import { EditorAttachmentsStrip } from '@/deskUiSkeletons/editor/components/Attachments/AttachmentsStrip'
import { useEditorAttachments } from '@/deskUiSkeletons/editor/useEditorAttachments'
import { EditorFormDefaults, HiddenEditorFormFields, useEditorTemplateContract } from '@/deskUiSkeletons/editor/EditorTemplateContract'
import { productDisplayComboValue } from '../../../productDisplayCatalog'

const forumProductValue = productDisplayComboValue('forum')
const forumCreationDefaults: ReadonlyArray<[EditorSettingName, boolean | number]> = [
  ['is_view', true],
  ['is_view_combo', forumProductValue],
]
const forumMessageCreationDefaults: ReadonlyArray<[EditorSettingName, boolean | number]> = [
  ...forumCreationDefaults,
  ['is_reg_users', true],
]

function HiddenAction({ name, label }: { name: Parameters<typeof contract>[0]; label: string }) {
  return <button type="button" {...contract(name)} className="hidden" hidden aria-hidden="true" tabIndex={-1}>{label}</button>
}

// Configure and Attachments ride the shared unified modals; the former
// inline form/settings swap is retired.
export function ForumEditorTemplate({
  active,
  editorMode,
  settingsOpen,
  onCloseSettings,
}: {
  active: boolean
  editorMode?: EditorMode
  settingsOpen: boolean
  onCloseSettings: () => void
}) {
  const formModel = useEditorTemplateContract(editorMode)
  const [attachmentsOpen, setAttachmentsOpen] = useState(false)
  const attachCount = editorAttachmentCount(useEditorAttachments(), editorMode)
  const defaultsApplied = useRef('')

  useEffect(() => {
    const isCreation = editorMode === 'new_answer' || editorMode === 'new_submsg' || editorMode === 'new_msg'
    if (!active || !isCreation) {
      defaultsApplied.current = ''
      return
    }
    const key = `${editorMode}:${forumProductValue}`
    if (defaultsApplied.current === key || !formModel.rough) return
    const defaults = editorMode === 'new_submsg' || editorMode === 'new_msg'
      ? forumMessageCreationDefaults
      : forumCreationDefaults
    const applied = defaults.every(([name, value]) => dispatchEditorAction({
      action: 'set-more-setting',
      name,
      value,
      reason: 'forum-surface:creation-defaults',
    }))
    if (applied) defaultsApplied.current = key
  }, [active, editorMode, formModel.rough])

  return (
    <div {...legacyHookAttrs('editor.root', `md-forum-editor min-h-[calc(100vh-4rem)] overflow-hidden border border-[#006699] bg-white font-[Verdana,Arial,Helvetica,sans-serif] text-[11px] text-black${active ? '' : ' hidden'}`)} style={{ display: active ? 'flex' : 'none' }} aria-hidden={!active}>
      <div className="flex min-h-0 w-full flex-col">
        <div className="bg-[#006699] px-2 py-1 font-bold text-white">{editorMode === 'new_answer' ? 'Post a reply' : editorMode === 'new_submsg' || editorMode === 'new_msg' ? 'Create a topic' : 'Edit topic'}</div>
        {editorMode === 'update_msg' ? <div className="border-b border-[#006699] bg-[#ffffcc] px-2 py-2 text-[10px]">Forum root saving is intentionally disabled: the current save API cannot preserve the Forum document type.</div> : null}
        <EditorFormDefaults {...formModel} editorMode={editorMode} />
        <HeadShort />
        <button type="button" {...contract('editor.close')} className="hidden" hidden aria-hidden="true" tabIndex={-1}>Close</button>
        {/* EDITOR-PAGE-PADDING: page-like breathing room below the title bar. */}
        <form {...contract('editor.form')} className="flex min-h-0 flex-1 flex-col p-2 sm:p-4">
          <EditorHeadFields editorMode={editorMode} />
          <div className="flex flex-wrap items-center gap-3 px-2 py-2">
            <EditorAttachmentsStrip editorMode={editorMode} />
            <button
              type="button"
              className="inline-flex h-8 cursor-pointer items-center gap-2 border border-[#006699] bg-white px-2 text-[10px] text-[#006699] hover:bg-[#dfe6ef]"
              onClick={() => setAttachmentsOpen(true)}
            >
              Attachments{attachCount > 0 ? ` (${attachCount})` : ''}
            </button>
          </div>
          <EditorBodyFields />
          <HiddenEditorFormFields inputs={formModel.inputs} />
        </form>
        <div {...contract('editor.settings')} className="!hidden" hidden aria-hidden="true" />
        <div {...legacyHookAttrs('editor.actions', 'hidden')} hidden aria-hidden="true">
          <HiddenAction name="editor.action.save" label="Save" />
          <HiddenAction name="editor.action.draft" label="Draft" />
          <HiddenAction name="editor.action.delete" label="Delete" />
          <HiddenAction name="editor.action.clear" label="Clear" />
          <HiddenAction name="editor.action.saveAndGo" label="Save and go" />
          <HiddenAction name="editor.action.restore" label="Restore" />
        </div>
        <EditorConfigureModal open={settingsOpen} onClose={onCloseSettings} />
        <EditorAttachmentsModal open={attachmentsOpen} onClose={() => setAttachmentsOpen(false)} editorMode={editorMode} />
      </div>
    </div>
  )
}
